import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Button } from 'reactstrap';
import { planFromInventoryPath } from './inventoryDeepLink';
import { Colors } from '../../../libs/theme';

/**
 * Prevent's Plan surfaces: start a project for a managed product, and say how
 * many devices it covers (STRY0038414).
 *
 * The count and the action live in SEPARATE table columns — DEVICES and
 * BUGZERO PLAN — rather than stacked in one cell. A column of bare numerals
 * can be read down and compared; a count buried under a repeated link cannot.
 * The unit therefore lives in the column header, not on every row.
 *
 * Everything here is presentational. The gate is evaluated once per surface by
 * usePlanProjectGate and the counts once per surface by useDeviceCounts — a
 * row must never fetch either for itself.
 *
 * `count` semantics, shared by both cells: a number (including 0) is a real
 * answer; `null` means the backend cannot count this product (no adapter, no
 * count mode, or a CMDB failure); `undefined` means it has not answered yet.
 */

/** True when `count` is a real number we can show. 0 counts as real. */
const hasRealCount = count => typeof count === 'number' && Number.isFinite(count);

/**
 * The BUGZERO PLAN column header, in the logo's own two colours.
 *
 * Sourced from `libs/theme`'s brand palette rather than re-typing the hexes,
 * so the header tracks the brand if it ever moves. Falcon renders table
 * headers uppercase and the logo sets its wordmark in caps too, so this is the
 * logo's real casing, not a shouty approximation. "PLAN" stays in the
 * inherited header colour so the cell still reads as a column heading.
 */
export function BugZeroPlanLabel() {
  return (
    <>
      <span style={{ color: Colors.red }}>BUG</span>
      <span style={{ color: Colors.blue }}>ZERO</span> PLAN
    </>
  );
}

/**
 * The DEVICES cell: the fleet size for one managed product.
 *
 * Left-justified to match the other columns, with tabular figures so the
 * digits still line up down the column. An em dash means "we could not tell
 * you" — distinct from 0, which means "you own none of these". Renders nothing
 * at all for a user who cannot create projects: an unusable number is noise,
 * and a count must never linger on a Plan-disabled tenant's rows just because
 * one reached the cache before the gate settled.
 */
export function DeviceCountCell({ count, canCreatePlanProject }) {
  if (!canCreatePlanProject) return null;
  if (!hasRealCount(count)) return <span className="text-muted">&mdash;</span>;
  return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{count}</span>;
}

DeviceCountCell.propTypes = {
  count: PropTypes.number,
  canCreatePlanProject: PropTypes.bool
};

/**
 * The count as a sentence, for the single managed-product page.
 *
 * That page is not a table, so there is no column header to carry the unit and
 * the label has to stay inline.
 */
export function DeviceCountInline({ count, canCreatePlanProject }) {
  if (!canCreatePlanProject || !hasRealCount(count)) return null;
  return <div className="text-muted fs--1">{count === 1 ? '1 device' : `${count} devices`}</div>;
}

DeviceCountInline.propTypes = {
  count: PropTypes.number,
  canCreatePlanProject: PropTypes.bool
};

/**
 * The action cell: enters the existing inventory scan flow for this product.
 *
 * The label omits "Plan" because the column header already says BUGZERO PLAN;
 * on the product page, where there is no header, pass `withProductLabel` to
 * get the fuller wording back.
 */
export default function PlanProjectCell({ managedProduct, canCreatePlanProject, withProductLabel }) {
  const navigate = useNavigate();
  if (!canCreatePlanProject) return null;

  return (
    <Button
      color="falcon-default"
      size="sm"
      onClick={() => navigate(planFromInventoryPath(managedProduct))}
    >
      {withProductLabel ? 'Create Plan project' : 'Create project'}
    </Button>
  );
}

PlanProjectCell.propTypes = {
  managedProduct: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string
  }).isRequired,
  canCreatePlanProject: PropTypes.bool,
  withProductLabel: PropTypes.bool
};
